// --- Core Dependencies
import React, {useEffect, useRef, useState} from 'react';
import {debounce} from 'lodash';
import {useSelector} from 'react-redux';
import moment from 'moment';
// --- UI Components
import {Row, Col} from 'antd';
import JoditEditor from 'jodit-react';
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import Select from '@dtci/spellbook/Select';
import {MultiSelect} from '@dtci/spellbook/MultiSelect';
import {InputLabel} from '../../../../../../components/InputLabel/InputLabel';
// --- API and State Management
import {datastageApi} from '../../../../../../api';
import {useSharedSectionsActions} from '../../../../../../store/sharedSections/useSharedSections';
// --- Dialogs
import {useTagsDialog} from '../../../../../../components/dialogs/dialogs/tags';
import {useUploadDialog} from '../../../../../../components/dialogs/dialogs/upload';
// --- Utilities
import {useLoading} from '../../../../../../utils/hooks/useLoading';
import {config} from '../../../../../../utils/editorConfig';
// --- Styles
import './styles.scss';
import {startJobStatusTracker} from "../../../../../../utils/jobStatusTracker";
import {useToastNotification} from "../../../../../../components/ToastNotifications";
import {useDeleteDialog} from "../../../../../../components/dialogs/dialogs/delete";

/**
 * SharedSectionForm Component
 * Renders an editable form for a shared section, allowing users to modify details, tags, and attachments.
 *
 * @returns {JSX.Element} The Shared Section form component.
 */
export const SharedSectionForm = () => {
    const editorRef = useRef(null);
    const {section, clearanceMemoSectionTypes, tagOptions, clearanceMemoTags, modalMode} =
        useSelector((state) => state.sharedSections);
    const {openTagsDialog} = useTagsDialog();
    const {openUploadDialog} = useUploadDialog();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateSharedSections, updateSharedSectionsForm} = useSharedSectionsActions();
    const [isPublic, setIsPublic] = useState(section?.isPublic);
    const {openToast} = useToastNotification();
    const {openDeleteDialog} = useDeleteDialog();

    // Update tag IDs in the form whenever tags change
    useEffect(() => {
        const tagIds = clearanceMemoTags.map((tag) => tag.value);
        updateSharedSectionsForm({key: 'section', data: {tagIdList: tagIds}});
    }, [clearanceMemoTags, updateSharedSectionsForm]);

    // Load tag options asynchronously
    const loadTagOptions = debounce(async (inputValue, callback) => {
        if (inputValue.length <= 2) return callback([]);
        try {
            const response = await datastageApi.tags({q: inputValue}, [0, 24], ['id', 'DESC']);
            const tags =
                response?.content?.map(({tagName, id}) => ({label: tagName, value: id})) ?? [];
            updateSharedSections({tagOptions: tags});
            callback(tags);
        } catch (error) {
            console.error('Failed to load tag options:', error);
            callback([]);
        }
    }, 300);

    // Open the tags modal for creating a new tag
    const handleOpenTagsModal = async () => {
        try {
            openTagsDialog('', 'add', async (tag) => {
                setLoadingTrue();
                const newTag = await datastageApi.createTag(tag);
                if (newTag) {
                    updateSharedSections({
                        clearanceMemoTags: [
                            ...clearanceMemoTags,
                            {label: newTag.tagName, value: newTag.id},
                        ],
                    });
                }
                setLoadingFalse();
            });
        } catch (error) {
            console.error('Error opening tags modal:', error);
        }
    };

    // Toggle the public/private status of the section
    const togglePublicStatus = () => {
        setIsPublic((prevStatus) => {
            const newStatus = !prevStatus;
            updateSharedSectionsForm({key: 'section', data: {isPublic: newStatus}});
            return newStatus;
        });
    };

    // Open the upload dialog for adding attachments
    const handleOpenUploadDialog = () => {
        openUploadDialog('', async ({file}) => {
            if (!file) return;

            const formData = new FormData();
            Array.isArray(file)
                ? file.forEach((rawFile) => formData.append('files', rawFile))
                : formData.append('files', file);
            formData.append('clearanceMemoTOCId', 0);
            formData.append('clearanceMemoSectionId', section?.id);

            try {
                setLoadingTrue();
                const res = await datastageApi.uploadClearanceMemoAttachment(formData);

                startJobStatusTracker(res?.id, async (message, status) => {
                    setLoadingTrue();
                    openToast(message, status);

                    if (status === 'success') {
                        const updatedSection = await datastageApi.sharedSectionById(section?.id);
                        updateSharedSections({section: updatedSection});
                    }
                    setLoadingFalse();
                });
                setLoadingFalse();
            } catch (error) {
                console.error('Error uploading attachment:', error);
                setLoadingFalse();
            }
        });
    };

    const onOpenDeleteDialog = (data) => {
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to delete ${data?.attachmentName}`,
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    const res = await datastageApi.deleteAttachment(data.id, false);

                    if (res?.success === true) {
                        const updatedSection = await datastageApi.sharedSectionById(section?.id);
                        updateSharedSections({section: updatedSection});

                        openToast('Attachment removed successfully', 'success');
                    }
                } catch (error) {
                    console.error('Error removing attachment:', error);
                    openToast(
                        'Error removing attachment',
                        'error',
                    );
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    // Render the attachments list
    const renderAttachments = () =>
        section?.attachments?.map((doc) => (
            <div key={doc.id} className="attachment-item">
                <a
                    onClick={(e) => {
                        e.preventDefault();
                        downloadAttachment(doc?.rightslineId, doc?.attachmentName)
                    }}
                    style={{
                        fontSize: 16,
                        color: '#3611c8',
                    }}
                >
                    {doc.attachmentName}
                </a>
                <Button iconType="fa-regular fa-trash" sizeType="large" styleType="link" onClick={() => onOpenDeleteDialog(doc)}/>
            </div>
        ));

    return (
        <>
            {/* Top section for name, type, and action buttons */}
            <Row className="top-section">
                <Col>
                    <InputLabel label="Name">
                        <Input
                            className="name-input"
                            value={section?.title}
                            placeholder="Name"
                            onChange={(e) =>
                                updateSharedSectionsForm({
                                    key: 'section',
                                    data: {title: e.target.value},
                                })
                            }
                        />
                    </InputLabel>
                </Col>
                <Col>
                    <InputLabel label="Type">
                        <Select
                            className="type-input"
                            options={clearanceMemoSectionTypes}
                            isSearchable
                            value={clearanceMemoSectionTypes?.find(
                                (type) => type.value === section?.clearanceMemoSectionTypeId,
                            )}
                            onChange={(e) =>
                                updateSharedSectionsForm({
                                    key: 'section',
                                    data: {clearanceMemoSectionTypeId: e.value},
                                })
                            }
                        />
                    </InputLabel>
                </Col>
                <Col>
                    <div className="tags-container">
                        <InputLabel label="Tags"/>
                        <Button
                            className="create-button"
                            iconType="fa-solid fa-tag"
                            styleType="link"
                            onClick={handleOpenTagsModal}
                        >
                            Create Tag
                        </Button>
                    </div>
                    <MultiSelect
                        className="tag-input"
                        async
                        defaultOptions={tagOptions}
                        loadOptions={loadTagOptions}
                        isSearchable
                        isClearable
                        value={clearanceMemoTags}
                        onChange={(tags) => updateSharedSections({clearanceMemoTags: tags})}
                    />
                </Col>
                <Col>
                    {modalMode === 'edit' && (
                        <InputLabel label="Created By | On">
                            <Input
                                value={`${section?.createdBy?.fullName} | ${moment(
                                    section?.createdAt,
                                ).format('MM/DD/YYYY')}`}
                                disabled
                            />
                        </InputLabel>
                    )}
                </Col>
                <Col className="action-buttons-container">
                    <Button
                        className="action-button"
                        iconType={`fa-solid ${isPublic ? 'fa-eye' : 'fa-lock'}`}
                        styleType="link"
                        onClick={togglePublicStatus}
                    >
                        {isPublic ? 'Public' : 'Private'}
                    </Button>
                    <Button
                        className="action-button"
                        iconType="fa-solid fa-paperclip"
                        styleType="link"
                        onClick={handleOpenUploadDialog}
                    >
                        {section?.attachments?.length || 0}
                    </Button>
                </Col>
            </Row>

            {/* Rich text editor for section body */}
            <JoditEditor
                ref={editorRef}
                value={section?.body}
                config={config}
                tabIndex={1}
                onBlur={(newContent) =>
                    updateSharedSectionsForm({key: 'section', data: {body: newContent}})
                }
            />

            {/* Attachments section */}
            <div className="attachments-container">{renderAttachments()}</div>
        </>
    );
};
